/**
 * Inline data sources (Migration 062 / Delivery roadmap Tier 2.4).
 *
 * Backs the in-app data-grid editor: a small CSV/JSON store keyed
 * by (org, project, name) that flows can reference from
 * `load-csv` / `data-loop` steps without round-tripping through
 * external buckets.
 */
import { z } from 'zod';

export const DATA_SOURCE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
export const DATA_SOURCE_MAX_NAME_LEN = 120;

export const DataSourceColumnTypeSchema = z.enum(['string', 'number', 'boolean', 'date']);
export type DataSourceColumnType = z.infer<typeof DataSourceColumnTypeSchema>;

export const DataSourceColumnSchema = z.object({
  name: z.string().min(1).max(120),
  type: DataSourceColumnTypeSchema.default('string'),
  required: z.boolean().default(false),
});
export type DataSourceColumn = z.infer<typeof DataSourceColumnSchema>;

export const DataSourceSchemaShape = z.object({
  columns: z.array(DataSourceColumnSchema).min(1),
});
export type DataSourceSchema = z.infer<typeof DataSourceSchemaShape>;

export const PutDataSourceRequestSchema = z.object({
  name: z.string().min(1).max(DATA_SOURCE_MAX_NAME_LEN),
  /** Raw CSV (RFC 4180-ish: comma-separated, optional double-quote
   *  escaping). The server validates that the header row exists and
   *  that the body fits within DATA_SOURCE_MAX_BYTES. */
  contentCsv: z.string(),
  /** Optional; when omitted the server infers from the CSV. */
  schema: DataSourceSchemaShape.optional(),
});
export type PutDataSourceRequest = z.infer<typeof PutDataSourceRequestSchema>;

export const DataSourceRowSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  name: z.string(),
  /** Monotonically increases on every successful PUT — the runner
   *  uses this to invalidate cached copies. */
  version: z.number().int().min(1),
  /** Approximate; we don't store byte-perfect counts after gzip. */
  sizeBytes: z.number().int().min(0),
  schema: DataSourceSchemaShape,
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
  /** Returned only on the single-row GET; the list endpoint omits it
   *  to keep payloads small. */
  contentCsv: z.string().optional(),
});
export type DataSourceRow = z.infer<typeof DataSourceRowSchema>;

export const ListDataSourcesResponseSchema = z.object({
  rows: z.array(DataSourceRowSchema),
  totalCount: z.number().int().min(0),
});
export type ListDataSourcesResponse = z.infer<typeof ListDataSourcesResponseSchema>;
